import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { identityApi } from '../api/identityApi';
import { logError } from '../utils/logging';

function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const [setupToken, setSetupToken] = useState('');

  // guard: run once per token (handles StrictMode double-mount and token changes)
  const lastTokenRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Verification link is missing or invalid.');
      return;
    }

    if (lastTokenRef.current === token) return;
    lastTokenRef.current = token;

    const verifyEmail = async () => {
      try {
        const data = await identityApi.verifyEmail(token);

        setStatus('success');
        setSetupToken(data.setup_token || '');

        if (data.email) {
          localStorage.setItem('userEmail', data.email);
        }
      } catch (err) {
        setStatus('error');
        setError(err?.response?.data?.message || err?.message || 'Verification failed. Please try again.');
        logError(err, { context: 'VerifyEmail verifyEmail', token });
      }
    };

    verifyEmail();
  }, [token]);

  const handleSetupPasswordNow = () => {
    if (setupToken) {
      navigate(`/setup-password/${setupToken}`);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      }}
    >
      {/* Background gradient overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Container maxWidth="sm" sx={{ mt: 4, position: 'relative', zIndex: 1 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          {status === 'verifying' && (
              <>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Verifying your email...
                </Typography>
              </>
          )}

          {status === 'success' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Email verified successfully!
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  You can now set up your password.
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleSetupPasswordNow}
                    sx={{ mt: 1 }}
                    disabled={!setupToken}
                >
                  Set up password now
                </Button>
              </Alert>
          )}

          {status === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Verification Failed
                </Typography>
                <Typography>{error}</Typography>
              </Alert>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default VerifyEmail;
